import { useMemo, useState } from 'react';
import { Calendar as BigCalendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { enUS } from 'date-fns/locale/en-US';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
} from '@carbon/icons-react';

// --- SETUP DATE-FNS LOCALIZER ---
const locales = { 'en-US': enUS };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

// --- CUSTOM BRUTALIST TOOLBAR ---
const CustomToolbar = (toolbar: any) => {
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const goToBack = () => toolbar.onNavigate('PREV');
  const goToNext = () => toolbar.onNavigate('NEXT');
  const goToCurrent = () => toolbar.onNavigate('TODAY');

  // Get current month and year from toolbar.date
  const currentDate = toolbar.date;
  const selectedMonth = currentDate.getMonth();
  const selectedYear = currentDate.getFullYear();
  const currentYear = new Date().getFullYear();
  const startYear = 2026;

  const shortMonths = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  const handleMonthChange = (month: number, year: number) => {
    const newDate = new Date(year, month, 1);
    toolbar.onNavigate('DATE', newDate);
    setIsDatePickerOpen(false);
  };

  const isMonthDisabled = (monthIdx: number, year: number) => {
    const now = new Date();
    const currentMonthIdx = now.getMonth();
    const currentYr = now.getFullYear();
    const isFutureMonth =
      year > currentYr || (year === currentYr && monthIdx > currentMonthIdx);
    return isFutureMonth;
  };

  // Get filter props from toolbar
  const { showCustomEvents, showLeaves, showHolidays, showBirthdays, onToggleCustomEvents, onToggleLeaves, onToggleHolidays, onToggleBirthdays } =
    toolbar;

  return (
    <div className="flex justify-between items-center mb-4">
      <div className="flex border border-border bg-background rounded-none shadow-sm h-10 shrink-0 relative">
        <button
          onClick={goToBack}
          className="text-muted-foreground hover:bg-muted/10 h-full w-10 flex items-center justify-center border-r border-border transition-colors"
        >
          <ChevronLeft size={16} />
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDatePickerOpen(!isDatePickerOpen);
          }}
          className="flex items-center justify-center gap-2.5 px-6 hover:bg-muted/5 transition-colors group"
        >
          <CalendarIcon
            size={16}
            className="text-primary-base opacity-90 group-hover:scale-110 transition-transform"
          />
          <span className="text-sm font-bold text-foreground tabular-nums tracking-wider uppercase">
            {toolbar.label}
          </span>
        </button>
        <button
          onClick={goToNext}
          className="text-muted-foreground hover:bg-muted/10 h-full w-10 flex items-center justify-center border-l border-border transition-colors"
        >
          <ChevronRight size={16} />
        </button>

        {/* THE POPOVER MENU */}
        {isDatePickerOpen && (
          <div className="absolute left-0 top-12 z-50 w-64 bg-card border border-border shadow-xl p-4 animate-in fade-in zoom-in-95 duration-100 rounded-none">
            {/* Year Selector */}
            <div className="flex items-center justify-between mb-4 border-b border-border pb-3">
              <button
                onClick={() => {
                  const newYear = selectedYear - 1;
                  if (newYear >= startYear) {
                    handleMonthChange(selectedMonth, newYear);
                  }
                }}
                disabled={selectedYear <= startYear}
                className="p-1 hover:bg-muted/10 text-muted-foreground transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="font-bold text-foreground text-sm tracking-widest">
                {selectedYear}
              </span>
              <button
                onClick={() => {
                  const newYear = selectedYear + 1;
                  if (newYear <= currentYear) {
                    handleMonthChange(selectedMonth, newYear);
                  }
                }}
                disabled={selectedYear >= currentYear}
                className="p-1 hover:bg-muted/10 text-muted-foreground transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronRight size={16} />
              </button>
            </div>

            {/* Month Grid */}
            <div className="grid grid-cols-3 gap-2">
              {shortMonths.map((m, i) => {
                const disabled = isMonthDisabled(i, selectedYear);
                return (
                  <button
                    key={m}
                    onClick={() => {
                      if (!disabled) {
                        handleMonthChange(i, selectedYear);
                      }
                    }}
                    disabled={disabled}
                    className={`py-2.5 text-xs font-bold uppercase tracking-wider transition-colors border rounded-none ${
                      selectedMonth === i
                        ? 'bg-primary-base text-white border-primary-base'
                        : disabled
                          ? 'bg-transparent border-transparent text-muted-foreground/30 cursor-not-allowed'
                          : 'bg-transparent border-transparent hover:border-border hover:bg-muted/5 text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {m}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Filters on the right side */}
      {(onToggleCustomEvents || onToggleLeaves || onToggleHolidays || onToggleBirthdays) && (
        <div className="flex border border-border bg-background rounded-none shadow-sm h-10 shrink-0">
          {onToggleCustomEvents && (
            <label className="flex items-center gap-2 px-4 cursor-pointer group border-r border-border hover:bg-muted/5 transition-colors">
              <input
                type="checkbox"
                checked={showCustomEvents}
                onChange={onToggleCustomEvents}
                className="appearance-none w-3.5 h-3.5 border border-border rounded-none cursor-pointer"
                style={{
                  backgroundColor: showCustomEvents ? 'rgb(5, 150, 105)' : 'transparent',
                }}
              />
              <span className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground group-hover:text-foreground">
                Events
              </span>
            </label>
          )}
          {onToggleLeaves && (
            <label className="flex items-center gap-2 px-4 cursor-pointer group border-r border-border hover:bg-muted/5 transition-colors">
              <input
                type="checkbox"
                checked={showLeaves}
                onChange={onToggleLeaves}
                className="appearance-none w-3.5 h-3.5 border border-border checked:bg-primary-base rounded-none cursor-pointer"
              />
              <span className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground group-hover:text-foreground">
                Leaves
              </span>
            </label>
          )}
          {onToggleHolidays && (
            <label className="flex items-center gap-2 px-4 cursor-pointer group border-r border-border hover:bg-muted/5 transition-colors">
              <input
                type="checkbox"
                checked={showHolidays}
                onChange={onToggleHolidays}
                className="appearance-none w-3.5 h-3.5 border border-border rounded-none cursor-pointer"
                style={{
                  backgroundColor: showHolidays ? '#3b82f6' : 'transparent',
                }}
              />
              <span className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground group-hover:text-foreground">
                Holidays
              </span>
            </label>
          )}
          {onToggleBirthdays && (
            <label className="flex items-center gap-2 px-4 cursor-pointer group hover:bg-muted/5 transition-colors">
              <input
                type="checkbox"
                checked={showBirthdays}
                onChange={onToggleBirthdays}
                className="appearance-none w-3.5 h-3.5 border border-border rounded-none cursor-pointer"
                style={{
                  backgroundColor: showBirthdays ? '#ec4899' : 'transparent',
                }}
              />
              <span className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground group-hover:text-foreground">
                Birthdays
              </span>
            </label>
          )}
        </div>
      )}
    </div>
  );
};

// --- HIGH-CONTRAST EVENT COMPONENT ---
const CustomEvent = ({ event }: any) => {
  const isHoliday = event.type === 'holiday';

  if (isHoliday) {
    return (
      <div className="w-full h-full px-2 py-1 text-[11px] font-black uppercase tracking-wider truncate border-l-8 bahandi-event-holiday">
        {event.title}
      </div>
    );
  }

  // Custom event styling (user-created events with category-based colors)
  if (event.type === 'custom') {
    const color = event.category
      ? getCategoryColor(event.category)
      : event.color || {
          bg: '#10b981', // emerald-500
          border: 'rgb(5, 150, 105)', // custom green
          text: '#fff',
        };

    return (
      <div
        className="w-full h-full px-2 py-1 text-[11px] font-black uppercase tracking-wider truncate border-l-8 bahandi-event-custom"
        style={{
          backgroundColor: color.bg,
          borderLeftColor: 'rgb(5, 150, 105)', // Always use the green border
          color: color.text,
        }}
      >
        {event.title}
      </div>
    );
  }

  // Leave/Birthday event with dynamic color
  const color = event.color || {
    bg: '#3b82f6',
    border: '#2563eb',
    text: '#fff',
  };

  return (
    <div
      className="w-full h-full px-2 py-1 text-[11px] font-black uppercase tracking-wider truncate border-l-8"
      style={{
        backgroundColor: color.bg,
        borderLeftColor: color.border,
        color: color.text,
      }}
    >
      {event.title}
    </div>
  );
};

/**
 * Convert ISO date string from database to BigCalendar date
 * For DATE fields (not DATETIME), we need to preserve the calendar date regardless of timezone
 */
export function dbDateToCalendarDate(isoString: string): Date {
  const date = new Date(isoString);

  // Extract UTC date components
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  const day = date.getUTCDate();

  // Create UTC date at noon to avoid timezone boundary issues
  // Noon ensures that in any timezone (-12 to +14), the date won't shift
  return new Date(Date.UTC(year, month, day, 12, 0, 0, 0));
}

/**
 * Generate a consistent color for a leave event based on ID
 * Uses a hash function to assign colors so the same ID always gets the same color
 */
export function getLeaveColor(leaveId: string): {
  bg: string;
  border: string;
  text: string;
} {
  const colors = [
    { bg: '#ef4444', border: '#dc2626', text: '#fff' }, // red
    { bg: '#f97316', border: '#ea580c', text: '#fff' }, // orange
    { bg: '#f59e0b', border: '#d97706', text: '#fff' }, // amber
    { bg: '#84cc16', border: '#65a30d', text: '#fff' }, // lime
    { bg: '#10b981', border: '#059669', text: '#fff' }, // emerald
    { bg: '#14b8a6', border: '#0d9488', text: '#fff' }, // teal
    { bg: '#06b6d4', border: '#0891b2', text: '#fff' }, // cyan
    { bg: '#3b82f6', border: '#2563eb', text: '#fff' }, // blue
    { bg: '#6366f1', border: '#4f46e5', text: '#fff' }, // indigo
    { bg: '#8b5cf6', border: '#7c3aed', text: '#fff' }, // violet
    { bg: '#a855f7', border: '#9333ea', text: '#fff' }, // purple
    { bg: '#ec4899', border: '#db2777', text: '#fff' }, // pink
  ];

  // Simple hash function to consistently assign colors based on leaveId
  let hash = 0;
  for (let i = 0; i < leaveId.length; i++) {
    hash = leaveId.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % colors.length;
  return colors[index]!;
}

export function getCategoryColor(category: string): {
  bg: string;
  border: string;
  text: string;
} {
  const categoryColors = {
    CTRLSPIRE_MEETING: { bg: '#3b82f6', border: '#2563eb', text: '#fff' }, // blue
    SWELLFOX_MEETING: { bg: '#8b5cf6', border: '#7c3aed', text: '#fff' }, // violet
    CLIENT_MEETING: { bg: '#10b981', border: '#059669', text: '#fff' }, // emerald
    FUN_HOUR: { bg: '#f59e0b', border: '#d97706', text: '#fff' }, // amber
    TEAM_BUILDING: { bg: '#ec4899', border: '#db2777', text: '#fff' }, // pink
    ANNOUNCEMENT: { bg: '#ef4444', border: '#dc2626', text: '#fff' }, // red
  };

  return categoryColors[category as keyof typeof categoryColors] || {
    bg: '#10b981',
    border: '#059669',
    text: '#fff',
  };
}

export interface BahandiCalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  type: 'holiday' | 'leave' | 'birthday' | 'custom';
  category?: 'CTRLSPIRE_MEETING' | 'SWELLFOX_MEETING' | 'CLIENT_MEETING' | 'FUN_HOUR' | 'TEAM_BUILDING' | 'ANNOUNCEMENT';
  allDay: true;
  status?: string;
  createdBy?: string;
  color?: {
    bg: string;
    border: string;
    text: string;
  };
}

export interface BahandiCalendarProps {
  events: BahandiCalendarEvent[];
  currentDate: Date;
  onNavigate: (date: Date) => void;
  onSelectSlot?: (slotInfo: { start: Date; end: Date }) => void;
  onSelectEvent?: (event: BahandiCalendarEvent) => void;
  selectable?: boolean;
  userTimezone?: string;
  showCustomEvents?: boolean;
  showLeaves?: boolean;
  showHolidays?: boolean;
  showBirthdays?: boolean;
  onToggleCustomEvents?: () => void;
  onToggleLeaves?: () => void;
  onToggleHolidays?: () => void;
  onToggleBirthdays?: () => void;
}

export function BahandiCalendar({
  events,
  currentDate,
  onNavigate,
  onSelectSlot,
  onSelectEvent,
  selectable = false,
  userTimezone = 'Asia/Manila',
  showCustomEvents,
  showLeaves,
  showHolidays,
  showBirthdays,
  onToggleCustomEvents,
  onToggleLeaves,
  onToggleHolidays,
  onToggleBirthdays,
}: BahandiCalendarProps) {
  // Sort events by date first, then by priority within the same day
  const sortedEvents = useMemo(() => {
    return [...events].sort((a, b) => {
      // First, sort by start date
      const dateCompare = a.start.getTime() - b.start.getTime();
      if (dateCompare !== 0) return dateCompare;

      // Then, within same day, sort by type priority
      const priority = { holiday: 0, leave: 1, custom: 2, birthday: 3 };
      return priority[a.type] - priority[b.type];
    });
  }, [events]);

  const customDayPropGetter = (date: Date) => {
    const isOffRange = date.getMonth() !== currentDate.getMonth();
    const isWeekend = date.getDay() === 0 || date.getDay() === 6;

    if (isOffRange) return { className: 'bahandi-off-range' };
    if (isWeekend) return { className: 'bahandi-weekend' };
    return {};
  };

  return (
    <>
      <div className="bg-card border-2 border-border p-2 rounded-none shadow-sm bahandi-rbc">
        <BigCalendar
          localizer={localizer}
          events={sortedEvents}
          startAccessor="start"
          endAccessor="end"
          defaultView="month"
          views={['month']}
          date={currentDate}
          onNavigate={onNavigate}
          style={{ height: 700 }}
          dayPropGetter={customDayPropGetter}
          selectable={selectable}
          onSelectSlot={onSelectSlot}
          onSelectEvent={onSelectEvent}
          popup={true}
          components={{
            toolbar: (props: any) => (
              <CustomToolbar
                {...props}
                showCustomEvents={showCustomEvents}
                showLeaves={showLeaves}
                showHolidays={showHolidays}
                showBirthdays={showBirthdays}
                onToggleCustomEvents={onToggleCustomEvents}
                onToggleLeaves={onToggleLeaves}
                onToggleHolidays={onToggleHolidays}
                onToggleBirthdays={onToggleBirthdays}
              />
            ),
            event: CustomEvent,
          }}
        />
      </div>

      {/* GLOBAL OVERLAY FIX */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
        .rbc-overlay {
          position: absolute !important;
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
          background: white !important;
          border: 2px solid black !important;
          box-shadow: 0 8px 16px rgba(0, 0, 0, 0.5) !important;
          padding: 16px !important;
          z-index: 999999 !important;
          min-width: 250px !important;
          pointer-events: auto !important;
        }
      `,
        }}
      />

      {/* BAHANDI-SPECIFIC BRUTALIST CSS OVERRIDES */}
      <style>{`
        .bahandi-rbc .rbc-calendar { font-family: inherit; }

        /* --- GRID BORDERS --- */
        .bahandi-rbc .rbc-month-view,
        .bahandi-rbc .rbc-header,
        .bahandi-rbc .rbc-day-bg,
        .bahandi-rbc .rbc-month-row {
          border-color: var(--color-bahandi-10) !important;
        }

        .dark .bahandi-rbc .rbc-month-view,
        .dark .bahandi-rbc .rbc-header,
        .dark .bahandi-rbc .rbc-day-bg,
        .dark .bahandi-rbc .rbc-month-row {
          border-color: var(--color-bahandi-05) !important;
        }

        /* --- HEADERS (Mon, Tue, Wed) --- */
        .bahandi-rbc .rbc-header {
          text-transform: uppercase;
          font-size: 0.65rem;
          font-weight: 800;
          letter-spacing: 0.1em;
          padding: 10px 0;
          background: var(--color-bahandi-19);
          color: var(--color-bahandi-09);
        }

        .dark .bahandi-rbc .rbc-header {
          background: var(--color-bahandi-02);
          color: var(--color-bahandi-11);
        }

        /* --- ACTIVE DAY NUMBERS --- */
        .bahandi-rbc .rbc-date-cell {
          font-weight: 900;
          font-size: 0.85rem;
          padding: 8px 8px 4px 8px;
        }

        /* --- THE OFF-RANGE FIX --- */
        .bahandi-rbc .rbc-off-range-bg {
          background-color: var(--color-bahandi-17) !important;
        }

        .dark .bahandi-rbc .rbc-off-range-bg {
          background-color: var(--color-bahandi-03) !important;
        }

        .bahandi-rbc .rbc-off-range .rbc-date-cell {
          opacity: 0.4 !important;
          font-weight: 500 !important;
          color: var(--color-bahandi-11) !important;
        }

        /* --- WEEKEND SHADING (Darker) --- */
        .bahandi-rbc .bahandi-weekend {
          background-color: rgba(0, 0, 0, 0.04) !important;
        }
        .dark .bahandi-rbc .bahandi-weekend {
          background-color: rgba(0, 0, 0, 0.1) !important;
        }

        /* --- ADD EVENT ON CLICK --- */
        /* Make the entire row cell clickable, not just the background */
        .bahandi-rbc .rbc-month-row {
          position: relative;
        }

        .bahandi-rbc .rbc-day-bg:not(.rbc-off-range-bg) {
          cursor: pointer;
          transition: background-color 0.15s ease;
        }

        .bahandi-rbc .rbc-day-bg:not(.rbc-off-range-bg):hover {
          background-color: rgba(59, 130, 246, 0.05) !important;
        }

        .dark .bahandi-rbc .rbc-day-bg:not(.rbc-off-range-bg):hover {
          background-color: rgba(59, 130, 246, 0.1) !important;
        }

        /* Allow +X more link to be clickable */
        .bahandi-rbc .rbc-show-more {
          pointer-events: auto !important;
          position: relative;
          z-index: 5;
        }

        /* --- TODAY'S HIGHLIGHT (Golden Circle) --- */
        .bahandi-rbc .rbc-today {
          background-color: transparent !important;
        }

        /* Only highlight the current date (both rbc-now and rbc-current on the same cell) */
        .bahandi-rbc .rbc-date-cell.rbc-now.rbc-current button.rbc-button-link {
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          width: 32px !important;
          height: 32px !important;
          border-radius: 50% !important;
          background-color: #fbbf24 !important;
          color: #000 !important;
          font-weight: 900 !important;
        }

        .dark .bahandi-rbc .rbc-date-cell.rbc-now.rbc-current button.rbc-button-link {
          background-color: #f59e0b !important;
          color: #000 !important;
        }

        /* --- CUSTOM EVENT STYLING --- */
        .bahandi-rbc .rbc-event {
          background-color: transparent !important;
          border: none !important;
          padding: 0 !important;
          border-radius: 0 !important;
          cursor: default !important;
          pointer-events: none !important;
        }

        /* Make custom events clickable */
        .bahandi-rbc .rbc-event:has(.bahandi-event-custom) {
          cursor: pointer !important;
          pointer-events: auto !important;
        }

        .bahandi-event-custom {
          cursor: pointer !important;
          pointer-events: auto !important;
        }

        .bahandi-rbc .rbc-event-content {
          border-radius: 0 !important;
        }
    

        .rbc-overlay .rbc-event {
          border-radius: 0 !important;
          padding: 0 !important;
          margin-bottom: 3px !important;

          &:last-child {
            margin-bottom: 0 !important;
          }
        }

        .rbc-overlay .rbc-overlay-header {
          padding: 6px 8px 4px !important;
          
        }


        .bahandi-rbc .rbc-row-segment {
          padding: 0 2px 4px 2px;
        }

        /* Fix +X more button in dark mode */
        .bahandi-rbc .rbc-show-more {
          background: transparent !important;
          color: var(--color-primary-base) !important;
          font-weight: 600 !important;
        }

        .dark .bahandi-rbc .rbc-show-more {
          color: var(--color-primary-base) !important;
        }

        /* Popup overlay for +X more */
        .rbc-overlay {
          position: absolute !important;
          background: var(--color-bahandi-20) !important;
          border: 2px solid var(--color-border) !important;
          box-shadow: 0 8px 16px rgba(0, 0, 0, 0.3) !important;
          padding: 8px !important;
          z-index: 999999 !important;
          min-width: 250px !important;
          display: flex !important;
          flex-direction: column !important;
        }

        .dark .rbc-overlay {
          background: var(--color-bahandi-01) !important;
        }

        .rbc-overlay-header {
          font-weight: 700 !important;
          padding: 4px 0 8px 0 !important;
          border-bottom: 2px solid var(--color-border) !important;
          margin-bottom: 8px !important;
          order: -1 !important;
        }

        /* Order events in popup by type - using :has() selector */
        .rbc-overlay .rbc-event:has([title*="HBD"]) {
          order: 2 !important;
        }

        .rbc-overlay .rbc-event:has([title*=" - VL"]),
        .rbc-overlay .rbc-event:has([title*=" - ML"]),
        .rbc-overlay .rbc-event:has([title*=" - SL"]),
        .rbc-overlay .rbc-event:has([title*=" - EL"]) {
          order: 1 !important;
        }

        .rbc-overlay .rbc-event:not(:has([title*="HBD"])):not(:has([title*=" - VL"])):not(:has([title*=" - ML"])):not(:has([title*=" - SL"])):not(:has([title*=" - EL"])) {
          order: 0 !important;
        }

        /* Keep today circle visible */
        .bahandi-rbc .rbc-now .rbc-button-link {
          background-color: #f59e0b !important;
          color: #000 !important;
          position: relative !important;
          z-index: 2 !important;
        }

        /* Leave Events */
        .bahandi-event-leave {
          background-color: var(--color-primary-base);
          color: var(--color-bahandi-20);
          border-color: var(--color-primary-13);
        }

        /* Holiday Events - Blue border */
        .bahandi-event-holiday {
          background-color: var(--color-bahandi-06);
          color: var(--color-bahandi-20);
          border-color: #3b82f6;
        }

        .dark .bahandi-event-holiday {
          background-color: var(--color-bahandi-16);
          color: var(--color-bahandi-01);
          border-color: #60a5fa;
        }
      `}</style>
    </>
  );
}
